import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvent } from '@/hooks/useEvent';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { EmbeddedAssetViewer } from '@/components/assets/EmbeddedAssetViewer';
import { EventImageLightbox } from '@/components/assets/EventImageLightbox';
import { useAssetById } from '@/hooks/useAssetById';

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: event, isLoading, error } = useEvent(id!);
  const [lightboxState, setLightboxState] = useState<{
    isOpen: boolean;
    assetId: string | null;
    currentImage: 'before' | 'after';
  }>({
    isOpen: false,
    assetId: null,
    currentImage: 'after'
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-red-300">
          <CardHeader>
            <CardTitle className="text-red-600">Error Loading Event</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">Failed to load event details. Please try again.</p>
            <Button onClick={() => navigate('/diffs')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Diffs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Event Not Found</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">The event you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/diffs')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Diffs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get event type badge color
  const getEventTypeBadgeColor = (eventType?: string) => {
    switch (eventType) {
      case 'price':
        return 'bg-green-100 text-green-800';
      case 'plan':
        return 'bg-blue-100 text-blue-800';
      case 'feature':
        return 'bg-purple-100 text-purple-800';
      case 'discount':
        return 'bg-orange-100 text-orange-800';
      case 'metric':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get verification badge
  const getVerificationBadge = () => {
    if (event.verified) {
      return <Badge className="bg-green-100 text-green-800">Verified</Badge>;
    }
    if (event.partially_correct) {
      return <Badge className="bg-yellow-100 text-yellow-800">Partially Correct</Badge>;
    }
    if (event.invalid) {
      return <Badge className="bg-red-100 text-red-800">Invalid</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800">Unverified</Badge>;
  };

  // Handler to open lightbox when image is clicked
  const handleImageClick = (assetId: string, image: 'before' | 'after') => {
    setLightboxState({
      isOpen: true,
      assetId,
      currentImage: image
    });
  };

  // Handler to close lightbox
  const handleCloseLightbox = () => {
    setLightboxState({
      isOpen: false,
      assetId: null,
      currentImage: 'after'
    });
  };

  // Handler to navigate between images in lightbox
  const handleNavigateLightbox = (image: 'before' | 'after') => {
    setLightboxState(prev => ({
      ...prev,
      currentImage: image
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Event Detail</h1>
            <p className="text-gray-600 mt-2">
              {event.slug && <span className="font-medium">{event.slug}</span>}
              {event.v_before && event.v_after && (
                <span className="text-sm ml-2">
                  {event.v_before} → {event.v_after}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            {event.event_type && (
              <Badge className={getEventTypeBadgeColor(event.event_type)}>
                {event.event_type}
              </Badge>
            )}
            {getVerificationBadge()}
          </div>
        </div>
      </div>

      {/* Event Metadata */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Event Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {event.description && (
              <div>
                <span className="font-medium text-gray-700">Description:</span>
                <p className="text-gray-600 mt-1">{event.description}</p>
              </div>
            )}
            {event.created_at && (
              <div>
                <span className="font-medium text-gray-700">Created:</span>
                <span className="text-gray-600 ml-2">
                  {format(parseISO(event.created_at), 'MMM d, yyyy h:mm a')}
                </span>
              </div>
            )}
            {event.updated_at && (
              <div>
                <span className="font-medium text-gray-700">Updated:</span>
                <span className="text-gray-600 ml-2">
                  {format(parseISO(event.updated_at), 'MMM d, yyyy h:mm a')}
                </span>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-700">Event ID:</span>
              <span className="text-gray-600 ml-2 font-mono text-sm">{event.id}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Before/After Image Comparison */}
      {event.asset_id && (
        <Card>
          <CardHeader>
            <CardTitle>Before & After Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[600px]">
              <EmbeddedAssetViewer
                assetId={event.asset_id}
                imageType="cropped"
                hideTypeToggle={false}
                onImageClick={(image) => handleImageClick(event.asset_id!, image)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Asset Message */}
      {!event.asset_id && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="pt-6">
            <p className="text-yellow-800">
              No visual asset is associated with this event.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Image Lightbox */}
      {lightboxState.isOpen && lightboxState.assetId && event && (
        <LightboxWithAsset
          assetId={lightboxState.assetId}
          currentImage={lightboxState.currentImage}
          imageType="cropped"
          description={event.description}
          onClose={handleCloseLightbox}
          onNavigate={handleNavigateLightbox}
        />
      )}
    </div>
  );
};

// Helper component to fetch asset and render lightbox
const LightboxWithAsset: React.FC<{
  assetId: string;
  currentImage: 'before' | 'after';
  imageType: 'markup' | 'cropped' | 'original';
  description?: string | null;
  onClose: () => void;
  onNavigate: (image: 'before' | 'after') => void;
}> = ({ assetId, currentImage, imageType, description, onClose, onNavigate }) => {
  const { asset, loading } = useAssetById(assetId);

  if (loading || !asset) {
    return null;
  }

  // Helper to get URLs based on imageType
  const getImageUrl = (type: 'primary' | 'secondary') => {
    const isSecondary = type === 'secondary';
    switch (imageType) {
      case 'markup':
        return isSecondary
          ? (asset.secondary_markup_url || asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_markup_url || asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
      case 'cropped':
        return isSecondary
          ? (asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
      case 'original':
        return isSecondary
          ? (asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_original_url || asset.primary_image);
      default:
        return isSecondary
          ? (asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
    }
  };

  // Determine chronological order
  const primaryDate = asset.version ? parseInt(asset.version) : 0;
  const secondaryDate = asset.secondary_version ? parseInt(asset.secondary_version) : 0;
  const shouldSwapForChronology = primaryDate > secondaryDate && getImageUrl('secondary');

  const beforeImageUrl = shouldSwapForChronology ? getImageUrl('secondary') : getImageUrl('primary');
  const afterImageUrl = shouldSwapForChronology ? getImageUrl('primary') : getImageUrl('secondary');
  const beforeVersion = shouldSwapForChronology ? asset.secondary_version : asset.version;
  const afterVersion = shouldSwapForChronology ? asset.version : asset.secondary_version;

  return (
    <EventImageLightbox
      isOpen={true}
      beforeImageUrl={beforeImageUrl}
      afterImageUrl={afterImageUrl}
      currentImage={currentImage}
      beforeVersion={beforeVersion}
      afterVersion={afterVersion}
      assetTitle={asset.title || asset.slug}
      imageType={imageType}
      description={description}
      onClose={onClose}
      onNavigate={onNavigate}
    />
  );
};

export default EventDetail;
